import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Compass, GraduationCap, ShoppingBag, TrendingUp, Quote, Check, Home as HomeIcon, Car, Info, Utensils, Sparkles } from "lucide-react";
import { api, photoUrl, fileUrl } from "@/lib/api";
import PropertyCard from "@/components/site/PropertyCard";
import AdSlot from "@/components/site/AdSlot";
import Seo from "@/components/site/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SECTIONS = [["lifestyle", "Estilo de vida", Sparkles], ["residential", "Tipo de viviendas", HomeIcon], ["restaurants", "Restaurantes y comercios", Utensils],
  ["schools", "Colegios y servicios", GraduationCap], ["mobility", "Movilidad y accesibilidad", Car], ["moving_info", "Consejos para mudarte", Info]];

const img = (x) => (typeof x === "string" ? fileUrl(x) : photoUrl(x));

const Head = ({ icon: Icon, title }) => (
  <h2 className="text-2xl font-bold flex items-center gap-3"><span className="w-10 h-10 rounded-full bg-[#FF3333]/10 text-[#FF3333] grid place-items-center"><Icon size={18} /></span>{title}</h2>
);

const CardList = ({ items, icon, title, testid }) => items?.length > 0 && (
  <section className="space-y-5" data-testid={testid}>
    <Head icon={icon} title={title} />
    <div className="grid sm:grid-cols-2 gap-4">
      {items.map((a, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-300" data-testid={`${testid}-item-${i}`}>
          <h3 className="font-semibold">{a.title}</h3>
          {a.text && <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{a.text}</p>}
        </div>
      ))}
    </div>
  </section>
);

const Residents = ({ notes }) => notes?.length > 0 && (
  <section className="rounded-3xl bg-[#121316] text-white p-8 sm:p-10 space-y-6" data-testid="zone-residents">
    <div className="flex items-center gap-3"><Quote className="text-[#FF3333]" size={28} /><h2 className="text-2xl font-bold">Lo que más valoran quienes viven aquí</h2></div>
    <ul className="grid sm:grid-cols-2 gap-4">
      {notes.map((n, i) => <li key={i} className="flex gap-3 text-white/85 leading-relaxed"><Check size={18} className="text-[#FF3333] shrink-0 mt-1" />{n?.text ?? n}</li>)}
    </ul>
  </section>
);

const Hero = ({ z }) => (
  <section className="relative min-h-[520px] flex items-end">
    <img src={img(z.hero_image)} alt={`${z.name}, Punta Cana`} className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />
    <div className="relative container-x pb-12 pt-32 text-white space-y-4">
      <Link to="/#zonas" className="text-xs uppercase tracking-[0.25em] font-semibold text-white/80 hover:text-white" data-testid="zone-back-link">Zonas de Punta Cana</Link>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold" data-testid="zone-title">{z.name}</h1>
      {z.tagline && <p className="text-lg sm:text-xl font-medium text-white/95" data-testid="zone-tagline">{z.tagline}</p>}
      {z.description && <p className="max-w-2xl text-white/80">{z.description}</p>}
      {z.highlights?.length > 0 && <div className="flex flex-wrap gap-2 pt-2" data-testid="zone-highlights">{z.highlights.map((h) => <span key={h} className="rounded-full bg-white/15 backdrop-blur-md border border-white/25 px-3.5 py-1.5 text-sm">{h}</span>)}</div>}
    </div>
  </section>
);

export default function ZonePage() {
  const { slug } = useParams();
  const [z, setZ] = useState(null);
  const [props, setProps] = useState([]);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setZ(null);
    api.get(`/public/zones/${slug}`).then((r) => {
      setZ(r.data);
      api.get("/public/properties", { params: { zone: r.data.name, limit: 6 } }).then((x) => setProps(x.data.items));
    }).catch(() => setErr(true));
    window.scrollTo(0, 0);
  }, [slug]);
  if (err) return <div className="container-x py-24 text-center"><h1 className="text-2xl font-bold">Zona no encontrada</h1></div>;
  if (!z) return <div className="container-x py-24 text-slate-500">Cargando...</div>;
  const sections = SECTIONS.filter(([k]) => z[k]);
  return (
    <div data-testid="zone-page">
      <Seo title={`Vivir y alquilar en ${z.name}, Punta Cana`} description={`Guía de ${z.name}: qué hacer, colegios, plazas, crecimiento y propiedades en alquiler de largo plazo. ${z.description || ""}`} image={img(z.hero_image)}
        jsonLd={{ "@context": "https://schema.org", "@type": "Place", name: z.name, description: z.description, containedInPlace: { "@type": "Place", name: "Punta Cana, República Dominicana" } }} />
      <Hero z={z} />
      <div className="container-x py-16 grid lg:grid-cols-[1.6fr_1fr] gap-12">
        <div className="space-y-14 min-w-0">
          <Residents notes={z.resident_notes} />
          <CardList items={z.activities} icon={Compass} title="Qué hacer en la zona" testid="zone-activities" />
          <CardList items={z.schools_list} icon={GraduationCap} title="Colegios y salud cercanos" testid="zone-schools" />
          <CardList items={z.shopping} icon={ShoppingBag} title="Plazas y comercios cercanos" testid="zone-shopping" />
          {z.growth && <section className="space-y-4 rounded-3xl bg-[#FF3333]/5 border border-[#FF3333]/15 p-8" data-testid="zone-growth"><Head icon={TrendingUp} title="Crecimiento de la zona" /><p className="text-slate-700 leading-relaxed">{z.growth}</p></section>}
          {sections.map(([k, l, I]) => (
            <section key={k} className="space-y-3" data-testid={`zone-section-${k}`}><Head icon={I} title={l} /><p className="text-slate-600 leading-relaxed whitespace-pre-line">{z[k]}</p></section>
          ))}
          {z.amenities?.length > 0 && <section className="space-y-3"><h2 className="text-2xl font-bold">Amenidades</h2><div className="flex flex-wrap gap-2">{z.amenities.map((a) => <span key={a} className="rounded-full bg-white border border-slate-200 px-3 py-1.5 text-sm">{a}</span>)}</div></section>}
          {z.gallery?.length > 0 && <section className="space-y-4" data-testid="zone-gallery"><h2 className="text-2xl font-bold">Así se vive en {z.name}</h2><div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{z.gallery.map((g, i) => <img key={i} src={img(g)} alt={`${z.name} ${i + 1}`} loading="lazy" className={`rounded-2xl object-cover w-full h-full ${i === 0 && z.gallery.length >= 3 ? "col-span-2 row-span-2 aspect-square" : "aspect-[4/3]"}`} />)}</div></section>}
          {z.faqs?.length > 0 && <section className="space-y-3"><h2 className="text-2xl font-bold">Preguntas frecuentes</h2>
            <Accordion type="single" collapsible>{z.faqs.map((f, i) => <AccordionItem key={i} value={`z${i}`}><AccordionTrigger className="text-left">{f.q}</AccordionTrigger><AccordionContent>{f.a}</AccordionContent></AccordionItem>)}</Accordion></section>}
          {(z.sources || z.info_updated_at) && <p className="text-xs text-slate-400 border-t pt-4" data-testid="zone-sources">{z.sources}{z.info_updated_at && <> Actualizado: {z.info_updated_at}.</>}</p>}
        </div>
        <aside className="space-y-6 lg:sticky lg:top-24 self-start">
          <div className="rounded-2xl overflow-hidden border border-slate-200 aspect-square bg-slate-100">
            <iframe title={`Mapa ${z.name}`} className="w-full h-full" loading="lazy" src={`https://www.google.com/maps?q=${encodeURIComponent(z.map_query || z.name)}&output=embed`} data-testid="zone-map" />
          </div>
          <Link to={`/propiedades?zone=${encodeURIComponent(z.name)}`} className="block text-center rounded-full bg-[#FF3333] hover:bg-[#E62E2E] text-white px-6 py-3.5 font-semibold transition-colors" data-testid="zone-see-properties-btn">Ver alquileres en {z.name}</Link>
          <AdSlot placement="zona" zone={z.name} emptyText={false} testid="zone-ads" />
        </aside>
      </div>
      <section className="container-x pb-8 space-y-6" data-testid="zone-properties">
        <div className="flex justify-between items-end gap-4"><h2 className="text-2xl font-bold">Propiedades en {z.name}</h2>
          <Link to={`/propiedades?zone=${encodeURIComponent(z.name)}`} className="font-semibold text-[#FF3333] shrink-0">Ver todas</Link></div>
        <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">{props.map((p) => <PropertyCard key={p.code} p={p} />)}</div>
        {!props.length && <p className="text-slate-500">No hay propiedades disponibles en este momento.</p>}
      </section>
    </div>
  );
}
